import Dates from 'utils/Dates'
import {
    getActiveOfferCalculations,
    getOfferCalculationCount,
    getOfferCalculationLabel,
    getOfferPrimaryPackagesCart,
    getPackagesCartHoursBreakdownMeta,
    hasOfferReservation,
    isTargetOfferCalculation,
    shouldShowOfferCalculationSubRows,
    sortOfferCalculationsForDisplay,
} from 'utils/offers/offersUtils'
import {getCompanyDisplayName} from 'utils/companyUtils'
import {getPackagesCartPersonsCount} from 'utils/utility'
import {Offer} from 'types/entities/Offer'
import {OfferListRow, OfferRowGroup} from 'components/leads/EditLeadModalComponents/LeadOffersListTypes'
import type {ColumnFilter} from '@tanstack/react-table'

// ─── Formatting helpers ──────────────────────────────────────────────────────

export const formatPackagesCartTime = (hour?: number | null, minute?: number | null): string | null => {
    if (hour === undefined || hour === null || minute === undefined || minute === null) return null
    return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

export const getMinutesFromTime = (value?: string | null): number | null => {
    if (!value || !/^\d{2}:\d{2}$/.test(value)) return null
    const [hours, minutes] = value.split(':').map((item) => Number(item))
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return null
    return hours * 60 + minutes
}

// ─── Row group builder ────────────────────────────────────────────────────────

export const buildOfferRowGroups = (offers: Offer[], t: (key: string, opts?: Record<string, unknown>) => string): OfferRowGroup[] =>
    offers.map((offer) => {
        const companyName = getCompanyDisplayName(offer.company)
        const primaryPackagesCart = getOfferPrimaryPackagesCart(offer)
        const primaryDurationBreakdown = getPackagesCartHoursBreakdownMeta(primaryPackagesCart)
        const activeCalculations = sortOfferCalculationsForDisplay(
            getActiveOfferCalculations(offer.calculations)
        )
        const calculationsCount = getOfferCalculationCount(offer)

        const parent: OfferListRow = {
            id: `offer-${offer.id}`,
            rowType: 'offer',
            offer,
            calculation: null,
            calculationIndex: null,
            guid: offer.guid || '-',
            company_name: companyName || '-',
            duration_breakdown: primaryDurationBreakdown.label,
            duration_karting_hours: primaryDurationBreakdown.kartingHours,
            duration_non_karting_hours: primaryDurationBreakdown.nonKartingHours,
            date: primaryPackagesCart?.date ?? offer.date ?? null,
            event_time: formatPackagesCartTime(primaryPackagesCart?.hour, primaryPackagesCart?.minute),
            persons: getPackagesCartPersonsCount(primaryPackagesCart),
            status: offer.status ?? '-',
            has_reservation: hasOfferReservation(offer) ? 'yes' : 'no',
            probability: offer.probability ?? null,
            valid_until: offer.valid_until ?? null,
            value: primaryPackagesCart?.price ?? 0,
            offer_number: offer.number ?? null,
            created_at: offer.created_at ?? primaryPackagesCart?.created_at ?? null,
            updated_at: offer.updated_at ?? primaryPackagesCart?.updated_at ?? null,
            company_type: offer.company?.type ?? null,
            calculations_count: calculationsCount,
            calculation_label: null,
            is_target: false,
        }

        const children: OfferListRow[] = shouldShowOfferCalculationSubRows(offer)
            ? activeCalculations.map((calculation, calculationIndex) => {
                  const calcDurationBreakdown = getPackagesCartHoursBreakdownMeta(calculation.packagesCart)
                  return {
                      id: `offer-${offer.id}-calculation-${calculation.id}`,
                      rowType: 'calculation' as const,
                      offer,
                      calculation,
                      calculationIndex,
                      guid: offer.guid || '-',
                      company_name: companyName || '-',
                      duration_breakdown: calcDurationBreakdown.label,
                      duration_karting_hours: calcDurationBreakdown.kartingHours,
                      duration_non_karting_hours: calcDurationBreakdown.nonKartingHours,
                      date: calculation.date ?? calculation.packagesCart?.date ?? null,
                      event_time: formatPackagesCartTime(
                          calculation.hour ?? calculation.packagesCart?.hour,
                          calculation.minute ?? calculation.packagesCart?.minute
                      ),
                      persons: getPackagesCartPersonsCount(calculation.packagesCart),
                      status: offer.status ?? '-',
                      has_reservation: hasOfferReservation(offer) ? 'yes' : 'no',
                      probability: offer.probability ?? null,
                      valid_until: offer.valid_until ?? null,
                      value: calculation.packagesCart?.price ?? calculation.price_total ?? 0,
                      offer_number: offer.number ?? null,
                      created_at: calculation.created_at ?? null,
                      updated_at: calculation.updated_at ?? null,
                      company_type: offer.company?.type ?? null,
                      calculations_count: calculationsCount,
                      calculation_label: getOfferCalculationLabel(calculation, t, calculationIndex + 1),
                      is_target: isTargetOfferCalculation(offer, calculation),
                  }
              })
            : []

        return { parent, children }
    })

// ─── Filter matchers ──────────────────────────────────────────────────────────

export const normalizeText = (value: unknown): string => String(value ?? '').trim().toLowerCase()

export const matchesTextFilter = (value: unknown, filterValue: unknown): boolean => {
    if (filterValue === undefined || filterValue === null || filterValue === '') return true
    return normalizeText(value).includes(normalizeText(filterValue))
}

export const matchesNumberRange = (value: number, filterValue: unknown): boolean => {
    if (!filterValue || typeof filterValue !== 'object') return true
    const range = filterValue as { from?: number; to?: number }
    if (range.from !== undefined && value < Number(range.from)) return false
    if (range.to !== undefined && value > Number(range.to)) return false
    return true
}

export const matchesDateRange = (value: string | null, filterValue: unknown): boolean => {
    if (!filterValue || typeof filterValue !== 'object') return true
    const range = filterValue as { from?: string | Date; to?: string | Date }
    if (!range.from && !range.to) return true
    if (!value) return false
    const dateValue = Dates.dayjs(value)
    if (!dateValue.isValid()) return false
    if (range.from) {
        const from = Dates.dayjs(range.from)
        if (from.isValid() && dateValue.isBefore(from.startOf('day'))) return false
    }
    if (range.to) {
        const to = Dates.dayjs(range.to)
        if (to.isValid() && dateValue.isAfter(to.endOf('day'))) return false
    }
    return true
}

export const matchesTimeRange = (value: string | null, filterValue: unknown): boolean => {
    if (!filterValue || typeof filterValue !== 'object') return true
    const range = filterValue as { from?: string; to?: string }
    if (!range.from && !range.to) return true
    const rowMinutes = getMinutesFromTime(value)
    if (rowMinutes === null) return false
    const fromMinutes = getMinutesFromTime(range.from)
    const toMinutes = getMinutesFromTime(range.to)
    if (fromMinutes !== null && rowMinutes < fromMinutes) return false
    if (toMinutes !== null && rowMinutes > toMinutes) return false
    return true
}

// ─── Sort helper ──────────────────────────────────────────────────────────────

export const getComparableValue = (row: OfferListRow, sortId: string): string | number => {
    switch (sortId) {
        case 'id':
            return row.rowType === 'calculation' ? row.calculation?.id ?? 0 : row.offer.id
        case 'guid':
            return row.guid
        case 'company_name':
            return row.company_name
        case 'date':
        case 'created_at':
        case 'updated_at':
        case 'valid_until':
            return row[sortId] ? Dates.dayjs(row[sortId] as string).valueOf() : 0
        case 'duration_breakdown':
            return row.duration_breakdown
        case 'event_time':
            return getMinutesFromTime(row.event_time) ?? 0
        case 'persons':
        case 'value':
        case 'calculations_count':
            return row[sortId] as number
        case 'status':
        case 'has_reservation':
            return row[sortId] as string
        case 'probability':
            return row.probability ?? -1
        case 'offer_number':
            return row.offer_number ?? 0
        default:
            return ''
    }
}

// ─── Row filter factory ───────────────────────────────────────────────────────

export const createOfferRowMatcher = (
    columnFilters: ColumnFilter[],
    globalFilter: string,
    t: (key: string) => string
) =>
    (row: OfferListRow): boolean => {
        const normalizedGlobal = normalizeText(globalFilter)
        if (normalizedGlobal) {
            const searchableValues = [
                row.rowType === 'calculation' ? row.calculation?.id : row.offer.id,
                row.guid,
                row.company_name,
                row.duration_breakdown,
                row.offer_number,
                row.status,
                row.calculation_label,
                row.date,
                row.event_time,
                row.persons,
                row.value,
            ]
            if (!searchableValues.some((value) => normalizeText(value).includes(normalizedGlobal))) return false
        }

        return columnFilters.every((filter) => {
            switch (filter.id) {
                case 'id':
                    return matchesTextFilter(
                        row.rowType === 'calculation' ? row.calculation?.id ?? '' : row.offer.id,
                        filter.value
                    )
                case 'guid':
                case 'company_name':
                case 'duration_breakdown':
                case 'offer_number':
                case 'probability':
                case 'valid_until':
                    return matchesTextFilter(row[filter.id as keyof OfferListRow], filter.value)
                case 'calculations_count':
                    return typeof filter.value === 'object'
                        ? matchesNumberRange(row.calculations_count, filter.value)
                        : matchesTextFilter(row.calculations_count, filter.value)
                case 'date':
                case 'created_at':
                case 'updated_at':
                    return matchesDateRange(row[filter.id as keyof OfferListRow] as string | null, filter.value)
                case 'event_time':
                    return typeof filter.value === 'object'
                        ? matchesTimeRange(row.event_time, filter.value)
                        : matchesTextFilter(row.event_time, filter.value)
                case 'persons':
                    return typeof filter.value === 'object'
                        ? matchesNumberRange(row.persons, filter.value)
                        : matchesTextFilter(row.persons, filter.value)
                case 'value':
                    return typeof filter.value === 'object'
                        ? matchesNumberRange(row.value, filter.value)
                        : matchesTextFilter(row.value, filter.value)
                case 'status': {
                    if (Array.isArray(filter.value)) {
                        if (filter.value.length === 0) return true
                        return filter.value.includes(row.status)
                    }
                    return matchesTextFilter(row.status, filter.value)
                }
                case 'company_type': {
                    if (Array.isArray(filter.value)) {
                        if (filter.value.length === 0) return true
                        return row.company_type ? filter.value.includes(row.company_type) : false
                    }
                    return matchesTextFilter(row.company_type, filter.value)
                }
                case 'has_reservation':
                    return matchesTextFilter(
                        row.has_reservation === 'yes' ? t('Yes') : row.has_reservation === 'no' ? t('No') : '-',
                        filter.value
                    )
                default:
                    return true
            }
        })
    }
